// 2026-04-24: Flashcard page — 2 LUỒNG RIÊNG BIỆT.
//   - /flashcards?mode=learn  → CHỈ học thẻ mới (từ tile "Học kiến thức sản phẩm" HomePage)
//   - /flashcards?mode=review → CHỈ ôn thẻ đến hạn (từ nút "Học ngay" trong FlashcardProgressWidget)
//
// 2 luồng không mix: vào learn thì hết thẻ mới → về home (KHÔNG auto suggest ôn).
// Vào review thì hết due → về home (KHÔNG auto suggest học mới).
// Mỗi entry point một nhiệm vụ rõ ràng, tránh user confusion.
//
// Giữ nguyên:
//   - CardView reveal + 4 nút SM-2 (❌😬😄👑)
//   - Keyboard shortcut 1-4 + Space
//   - Sidebar ProgressPanel by bộ × chủ đề (context chung cho cả 2 mode)
import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api } from '../lib/api.js';
import { safeRichHtml } from '../lib/renderRichText.js';
import { boShort } from '../lib/bo.js';

const QUALITY_BUTTONS = [
  { q: 1, label: 'Quên hẳn',              short: 'Quên',     icon: '❌', color: 'rose',    kbd: '1' },
  { q: 2, label: 'Nhớ nhưng khó',         short: 'Khó',      icon: '😬', color: 'amber',   kbd: '2' },
  { q: 3, label: 'Nhớ dễ dàng',           short: 'Dễ',       icon: '😄', color: 'sky',     kbd: '3' },
  { q: 4, label: 'Nhớ cực kỳ dễ',         short: 'Siêu Dễ',  icon: '👑', color: 'emerald', kbd: '4' },
];

function ratingCls(color) {
  switch (color) {
    case 'rose':    return 'bg-rose-500 hover:bg-rose-600';
    case 'amber':   return 'bg-amber-500 hover:bg-amber-600';
    case 'sky':     return 'bg-sky-500 hover:bg-sky-600';
    case 'emerald': return 'bg-emerald-500 hover:bg-emerald-600';
    default:        return 'bg-slate-500 hover:bg-slate-600';
  }
}

const STATE_BADGE = {
  new:      { label: 'Mới',      cls: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  learning: { label: 'Đang học', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  review:   { label: 'Ôn',       cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
};

export default function FlashcardPage() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  // Mode cố định theo URL — NV vào từ 2 entry point khác nhau.
  // Default về 'learn' nếu URL không rõ (fail-safe cho bookmark cũ).
  const rawMode = searchParams.get('mode');
  const mode = rawMode === 'review' ? 'review' : 'learn';

  const [loading, setLoading] = useState(true);
  const [card, setCard] = useState(null);
  const [meta, setMeta] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [progress, setProgress] = useState(null);

  // ---- Progress panel ----
  const refreshProgress = useCallback(async () => {
    try {
      const p = await api.flashcardProgress();
      setProgress(p);
    } catch { /* panel phụ trợ */ }
  }, []);

  // ---- Fetch next card theo mode cố định ----
  const fetchNext = useCallback(async () => {
    try {
      const data = await api.flashcardNext(mode);
      setCard(data.card ? { ...data.card, labels: data.labels || {} } : null);
      setMeta(data.meta || null);
      setRevealed(false);
    } catch (err) {
      toast.error(err.data?.error || 'Không lấy được thẻ kế tiếp');
      setCard(null);
    }
  }, [mode]);

  // ---- Bootstrap ----
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      await Promise.all([fetchNext(), refreshProgress()]);
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [fetchNext, refreshProgress]);

  // ---- Submit review ----
  const doReview = useCallback(async (quality) => {
    if (!card || submitting) return;
    setSubmitting(true);
    const prevTarget = meta?.dailyMinTarget || 10;
    const prevLearned = meta?.new_cards_learned || 0;
    const wasBelowTarget = prevLearned < prevTarget;
    try {
      const res = await api.flashcardReview(card.card_id, quality, card.kind || 'question');
      setLastResult(res);

      // Toast cap quota mới
      if (res.was_new && res.quota?.new_cards_learned >= (meta?.quotaLimit || 30)) {
        toast('Đã hoàn thành quota thẻ mới hôm nay 🎉', { icon: '✅' });
      }
      // Toast chạm daily target
      if (res.was_new && wasBelowTarget && res.quota?.new_cards_learned >= prevTarget) {
        toast.success(`Chúc mừng bạn hoàn thành nhiệm vụ ngày hôm nay! (${prevTarget} thẻ mới)`, {
          duration: 4000, icon: '🎉',
        });
      }

      await Promise.all([fetchNext(), refreshProgress()]);
    } catch (err) {
      toast.error(err.data?.error || 'Ghi nhận review thất bại');
    } finally {
      setSubmitting(false);
    }
  }, [card, submitting, meta, fetchNext, refreshProgress]);

  // ---- Keyboard shortcuts ----
  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (!card) return;
      if (!revealed) {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          setRevealed(true);
        }
      } else {
        const n = parseInt(e.key, 10);
        if (n >= 1 && n <= 4 && !submitting) {
          e.preventDefault();
          doReview(n);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [card, revealed, submitting, doReview]);

  if (loading) {
    return (
      <div className="min-h-full p-6 flex items-center justify-center text-slate-400">
        Đang tải…
      </div>
    );
  }

  return (
    <div className={`min-h-full py-8 px-4 md:px-6 ${mode === 'review'
      ? 'bg-gradient-to-br from-amber-50/60 via-white to-orange-50/40'
      : 'bg-gradient-to-br from-brand-50/50 via-white to-indigo-50/30'}`}>
      <div className="max-w-7xl mx-auto">
        {/* Back button ở ngoài grid để card hero + sidebar cùng bắt đầu 1 level */}
        <button
          onClick={() => nav('/')}
          className="text-sm text-slate-500 hover:text-brand-700 transition inline-flex items-center gap-1 mb-4"
        >
          <span>←</span> Quay lại
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6">
          <div className="min-w-0">
            <TopHeader meta={meta} progress={progress} mode={mode} />

            {/* M153 (2026-05-13): banner trung thực US-L8 — hiển thị cả learn + review */}
            <HonestyBanner mode={mode} />

            {card ? (
              <CardView
                card={card}
                revealed={revealed}
                onReveal={() => setRevealed(true)}
                onReview={doReview}
                submitting={submitting}
              />
            ) : (
              <EmptyState meta={meta} mode={mode} onBackHome={() => nav('/')} />
            )}

            {lastResult && card && <LastReviewNote result={lastResult} />}
          </div>

          <aside className="lg:sticky lg:top-6 lg:self-start">
            <ProgressPanel data={progress} />
          </aside>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// M153 (2026-05-13): Banner trung thực — PRD US-L8.
// Hiển thị 2 dòng motivation cho NV trên cả mode learn + review.
// Tone: nhẹ nhàng (indigo/amber), KHÔNG phải warning đỏ — đây là khuyến khích
// tự đánh giá thật, không phải cảnh báo gian lận.
// ============================================================
function HonestyBanner({ mode }) {
  const isReview = mode === 'review';
  const palette = isReview
    ? 'bg-amber-50 border-amber-200 text-amber-900'
    : 'bg-indigo-50 border-indigo-200 text-indigo-900';
  return (
    <div className={`mb-5 rounded-2xl border px-4 py-3 text-sm leading-relaxed ${palette}`}>
      <div className="flex items-start gap-2.5">
        <span className="text-lg shrink-0" aria-hidden>💡</span>
        <div className="space-y-1.5">
          <p>
            <strong>Học là học cho bạn</strong> — nếu quên thì đừng ngần ngại bấm
            <span className="mx-1 px-1.5 py-0.5 rounded bg-white/70 font-semibold">❌ Quên</span>
            để có thể học lại nhanh hơn.
          </p>
          <p>
            Bạn đọc câu hỏi, trả lời câu hỏi <strong>trong đầu</strong>, đừng nhớ kiểu mang máng mà coi trả lời được rồi.
            Nếu nhớ sai hoặc không nhớ thì bấm vào <strong>Quên</strong>.
          </p>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// TopHeader — 2 context khác nhau theo mode (learn | review).
// 2026-04-24: Back button đã move ra ngoài grid → TopHeader chỉ còn card hero.
// ============================================================
function TopHeader({ meta, progress, mode }) {
  const isReview = mode === 'review';
  const newDone = meta?.new_cards_learned ?? 0;
  const newCap = meta?.quotaLimit ?? 30;
  const minTarget = meta?.dailyMinTarget ?? 10;
  const reviewsDone = meta?.reviews_done ?? 0;
  const dueNow = progress?.due_now ?? 0;

  // Progress ring ý nghĩa khác theo mode:
  //   learn:  % nhiệm vụ thẻ mới (newDone/minTarget)
  //   review: % đã ôn / tổng due đầu session (reviewsDone / (reviewsDone + dueNow))
  let goalPct, goalDone, goalLabel, goalSublabel;
  if (isReview) {
    const totalSession = reviewsDone + dueNow;
    goalPct = totalSession > 0 ? Math.round((reviewsDone / totalSession) * 100) : 0;
    goalDone = dueNow === 0;
    goalLabel = `${reviewsDone}/${totalSession}`;
    goalSublabel = 'Đã ôn';
  } else {
    goalPct = Math.min(100, Math.round((newDone / Math.max(1, minTarget)) * 100));
    goalDone = newDone >= minTarget;
    goalLabel = `${newDone}/${minTarget}`;
    goalSublabel = 'Nhiệm vụ';
  }

  const title = isReview ? 'Ôn tập thẻ đến hạn' : 'Học kiến thức sản phẩm';
  const titleIcon = isReview ? '🔁' : '📘';
  const subtitle = isReview
    ? (goalDone
        ? '🎉 Đã ôn hết thẻ đến hạn — tuyệt vời!'
        : `Còn ${dueNow} thẻ cần ôn lại — ôn kỹ để nhớ lâu hơn`)
    : (goalDone
        ? '🎉 Đã xong nhiệm vụ hôm nay — bạn vẫn có thể học thêm'
        : `Học thêm ${minTarget - newDone} thẻ mới để đạt nhiệm vụ hôm nay`);

  return (
    <header className="mb-5">
      <div className={`bg-white rounded-2xl border shadow-sm p-5 flex items-center gap-5 flex-wrap ${
        isReview ? 'border-amber-200' : 'border-slate-200'
      }`}>
        {/* Left: title + subtitle */}
        <div className="flex-1 min-w-[200px]">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-2xl">{titleIcon}</span>
            <h1 className="text-2xl font-bold text-slate-800">{title}</h1>
          </div>
          <p className="text-sm text-slate-500">{subtitle}</p>
        </div>

        {/* Right: progress ring + stat pills phù hợp mode */}
        <div className="flex items-center gap-4">
          <ProgressRing
            pct={goalPct}
            label={goalLabel}
            sublabel={goalSublabel}
            done={goalDone}
            color={isReview ? 'amber' : 'indigo'}
          />
          <div className="flex flex-col gap-1.5 text-xs">
            {isReview ? (
              <>
                <StatPill icon="🔁" label="Đã ôn session này" value={reviewsDone} color="emerald" />
                <StatPill icon="⏰" label="Còn đến hạn" value={dueNow} color={dueNow > 0 ? 'amber' : 'slate'} />
              </>
            ) : (
              <>
                <StatPill icon="📘" label="Thẻ mới hôm nay" value={`${newDone}/${newCap}`} color="indigo" />
                <StatPill icon="🎯" label="Mục tiêu tối thiểu" value={minTarget} color="emerald" />
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

// Progress ring SVG — circle với stroke dash.
function ProgressRing({ pct, label, sublabel, done, color = 'indigo' }) {
  const size = 80;
  const stroke = 8;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (pct / 100) * c;
  // Hardcode 2 màu để JIT compile đúng
  const strokeColor = done
    ? '#10b981'
    : color === 'amber' ? '#f59e0b' : '#6366f1';
  const textColor = done
    ? 'text-emerald-600'
    : color === 'amber' ? 'text-amber-700' : 'text-indigo-700';
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#e2e8f0" strokeWidth={stroke} />
        <circle
          cx={size/2} cy={size/2} r={r} fill="none"
          stroke={strokeColor}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          className="transition-all duration-500"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className={`text-[13px] font-bold tabular-nums ${textColor}`}>
          {label}
        </div>
        <div className="text-[9px] text-slate-400 leading-none">{sublabel}</div>
      </div>
    </div>
  );
}

function StatPill({ icon, label, value, color }) {
  const cls = {
    indigo:  'bg-indigo-50 text-indigo-700 border-indigo-100',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    amber:   'bg-amber-50 text-amber-700 border-amber-100',
    slate:   'bg-slate-50 text-slate-600 border-slate-100',
  }[color] || 'bg-slate-50 text-slate-600 border-slate-100';
  return (
    <div className={`${cls} border px-2.5 py-1 rounded-lg flex items-center gap-2 text-[11px]`}>
      <span className="text-sm">{icon}</span>
      <span className="font-medium">{label}:</span>
      <b className="tabular-nums">{value}</b>
    </div>
  );
}

// ============================================================
// CardView — 2026-04-24 redesign với hero typography + ring-2 accent
// ============================================================
function CardView({ card, revealed, onReveal, onReview, submitting }) {
  const stateKey = card.is_new ? 'new' : 'learning';
  const badge = STATE_BADGE[stateKey] || STATE_BADGE.new;

  // Ring màu theo state để card có cảm giác phân biệt
  const ringCls = card.is_new
    ? 'ring-2 ring-indigo-100'
    : 'ring-2 ring-emerald-100';

  return (
    <div className={`bg-white rounded-3xl shadow-lg ${ringCls} overflow-hidden transition-all`}>
      {/* Top strip — gradient theo state */}
      <div className={`h-1.5 ${card.is_new
        ? 'bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400'
        : 'bg-gradient-to-r from-emerald-400 via-teal-400 to-sky-400'}`}
      />

      <div className="p-6 md:p-8">
        {/* Tag row */}
        <div className="flex items-center justify-between flex-wrap gap-2 mb-5">
          <div className="flex items-center gap-2 text-xs">
            <span className={`px-2.5 py-1 rounded-full border font-medium ${badge.cls}`}>
              {badge.label}
            </span>
            <span className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 font-mono">
              {boShort(card.set_name)}
            </span>
            {card.topic && (
              <span className="text-slate-500 font-medium">· {card.topic}</span>
            )}
          </div>
        </div>

        {/* Question — bigger, more room */}
        <div
          className="rich-content text-[17px] md:text-lg font-semibold text-slate-800 leading-relaxed"
          dangerouslySetInnerHTML={safeRichHtml(card.question)}
        />

        {/* Answer or Reveal button */}
        {revealed ? (
          <div className="mt-5">
            {card.answer ? (
              <div className="relative">
                <div className="absolute -top-2.5 left-4 bg-white px-2 text-[10px] uppercase tracking-wide text-emerald-600 font-bold">
                  Đáp án
                </div>
                <div
                  className="rich-content p-5 rounded-2xl bg-gradient-to-br from-emerald-50 to-teal-50/50 border border-emerald-100 text-slate-700 leading-relaxed"
                  dangerouslySetInnerHTML={safeRichHtml(card.answer)}
                />
              </div>
            ) : (
              <div className="p-5 rounded-2xl bg-slate-50 border border-slate-200 text-center">
                <span className="italic text-slate-400">(Không có đáp án)</span>
              </div>
            )}
          </div>
        ) : (
          <div className="mt-6">
            <button
              onClick={onReveal}
              className="group w-full py-5 rounded-2xl bg-gradient-to-br from-brand-500 to-indigo-600 hover:from-brand-600 hover:to-indigo-700 text-white font-semibold shadow-md hover:shadow-lg transition-all active:scale-[0.99]"
              data-testid="fc-reveal"
            >
              <span className="inline-flex items-center gap-2">
                <span className="text-xl">👁️</span>
                <span className="text-base">Hiển thị đáp án</span>
                <kbd className="hidden md:inline-block ml-1 px-2 py-0.5 rounded bg-white/20 text-[10px] font-mono tracking-wider">Space</kbd>
              </span>
            </button>
          </div>
        )}

        {/* Quality buttons */}
        {revealed && (
          <div className="mt-6">
            <div className="text-[11px] text-slate-500 mb-2 text-center">
              Bạn nhớ câu này như thế nào?
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
              {QUALITY_BUTTONS.map((b) => {
                const label = card.labels?.[b.q] || '';
                return (
                  <button
                    key={b.q}
                    disabled={submitting}
                    onClick={() => onReview(b.q)}
                    data-testid={`fc-btn-${b.q}`}
                    title={b.label}
                    className={`${ratingCls(b.color)} group relative text-white font-semibold py-4 rounded-2xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex flex-col items-center shadow-md hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0`}
                  >
                    <span className="absolute top-1.5 right-2 text-[10px] font-mono opacity-60 group-hover:opacity-90">
                      {b.kbd}
                    </span>
                    <div className="text-3xl leading-none mb-1 transition-transform group-hover:scale-110">{b.icon}</div>
                    <div className="text-sm font-bold">{b.short}</div>
                    {label && (
                      <div className="text-[10px] opacity-90 mt-0.5 font-normal">{label}</div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Hint */}
        <div className="mt-5 pt-4 border-t border-slate-100 text-center text-[11px] text-slate-400">
          {revealed
            ? 'Bấm 1–4 để đánh giá · Nhãn dưới = thời gian đến lần ôn tiếp'
            : 'Bấm Space hoặc Enter để hiện đáp án'}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// EmptyState — 2 scenario rõ ràng theo mode, không cross-suggest
// ============================================================
function EmptyState({ meta, mode, onBackHome }) {
  const source = meta?.source || 'empty';
  const isReview = mode === 'review';

  let icon, title, msg, bgGradient;

  if (isReview) {
    // Review mode: hết card due
    icon = '✨';
    title = 'Đã ôn hết thẻ đến hạn!';
    msg = 'Tuyệt vời — bạn đã ôn xong mọi thẻ đến hạn lúc này. Thẻ sẽ tự xuất hiện lại khi đến lịch ôn kế tiếp.';
    bgGradient = 'from-amber-50 to-orange-50';
  } else {
    // Learn mode: hết thẻ mới (quota hoặc hết kho)
    if (source === 'quota_reached') {
      icon = '🎯';
      title = 'Đã đạt giới hạn thẻ mới hôm nay';
      msg = `Bạn đã học ${meta?.quotaLimit || 30} thẻ mới hôm nay (cap do admin đặt). Quay lại ngày mai để học tiếp.`;
      bgGradient = 'from-emerald-50 to-teal-50';
    } else {
      icon = '📭';
      title = 'Hết thẻ mới trong hệ thống';
      msg = 'Không còn thẻ mới nào chưa học trong kho. Liên hệ admin để thêm câu hỏi mới.';
      bgGradient = 'from-slate-50 to-slate-100';
    }
  }

  return (
    <div className={`bg-gradient-to-br ${bgGradient} rounded-3xl shadow-sm border border-white p-10 text-center`}>
      <div className="text-7xl mb-4 animate-bounce">{icon}</div>
      <h2 className="text-2xl font-bold text-slate-800 mb-2">{title}</h2>
      <p className="text-sm text-slate-600 max-w-md mx-auto mb-6 leading-relaxed">{msg}</p>
      <button
        onClick={onBackHome}
        className="px-5 py-2.5 rounded-xl bg-white border border-slate-300 hover:border-brand-400 text-slate-700 hover:text-brand-700 text-sm font-semibold shadow-sm hover:shadow transition-all"
      >
        ← Về trang chủ
      </button>
    </div>
  );
}

// ============================================================
// LastReviewNote — 2026-04-24 redesign: toast-style nhẹ + fade in
// 2026-04-24 (M38): thêm hint "Chưa tính 1 lần ôn" khi NV bấm Quên cho card cũ
// ============================================================
function LastReviewNote({ result }) {
  if (!result) return null;
  const qualityLabel = {
    1: { txt: '❌ Quên',     cls: 'bg-rose-100 text-rose-700 border-rose-200' },
    2: { txt: '😬 Khó',      cls: 'bg-amber-100 text-amber-700 border-amber-200' },
    3: { txt: '😄 Dễ',       cls: 'bg-sky-100 text-sky-700 border-sky-200' },
    4: { txt: '👑 Siêu Dễ',  cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  }[result.quality] || { txt: '?', cls: 'bg-slate-100 text-slate-600 border-slate-200' };

  // Card cũ + Quên = không tính 1 lần ôn hoàn thành (backend rule)
  const forgotReview = result.quality === 1 && !result.was_new;
  const nextDays = result.sm2?.interval_days;
  const learnAgainMinutes = result.sm2?.state === 'learning' && result.quality === 1 ? 10 : null;

  return (
    <div className="mt-4 flex flex-wrap items-center justify-center gap-2 px-4 py-2.5 rounded-2xl bg-white/80 backdrop-blur border border-slate-200 shadow-sm text-xs">
      <span className="text-slate-500">Thẻ trước:</span>
      <span className={`px-2.5 py-0.5 rounded-full font-semibold border ${qualityLabel.cls}`}>
        {qualityLabel.txt}
      </span>
      {result.quality === 1 && learnAgainMinutes ? (
        <span className="text-slate-500">
          Xuất hiện lại sau <b className="text-slate-700">{learnAgainMinutes}</b> phút
        </span>
      ) : nextDays != null && (
        <span className="text-slate-500">
          Ôn lại sau <b className="text-slate-700">{nextDays}</b> ngày
        </span>
      )}
      {result.was_new && (
        <span className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600 font-medium border border-indigo-100">
          +1 thẻ mới
        </span>
      )}
      {forgotReview && (
        <span
          className="px-2 py-0.5 rounded-full bg-rose-50 text-rose-600 font-medium border border-rose-100"
          title="Quên = không được tính là 1 lần ôn hoàn thành. Phải ôn lại."
        >
          ⚠ Chưa tính ôn
        </span>
      )}
    </div>
  );
}

// ============================================================
// ProgressPanel — 2026-04-24 redesign: gradient header, icon per bộ,
// collapsible per-bộ với animation, topic stacked bar đẹp hơn.
// ============================================================
// Static Tailwind classes (JIT compiler cần full class name, không dùng dynamic string)
const SET_META = {
  bo_1000k: {
    icon: '💰',
    gradient: 'from-indigo-500 to-blue-500',
    pctPill: 'bg-indigo-100 text-indigo-700',
  },
  bo_1250k: {
    icon: '💎',
    gradient: 'from-sky-500 to-cyan-500',
    pctPill: 'bg-sky-100 text-sky-700',
  },
  bo_nang_cao: {
    icon: '🚀',
    gradient: 'from-fuchsia-500 to-pink-500',
    pctPill: 'bg-fuchsia-100 text-fuchsia-700',
  },
  bo_noi_quy: {
    icon: '📜',
    gradient: 'from-amber-500 to-orange-500',
    pctPill: 'bg-amber-100 text-amber-700',
  },
};
const DEFAULT_SET_META = {
  icon: '📘',
  gradient: 'from-brand-500 to-indigo-500',
  pctPill: 'bg-brand-100 text-brand-700',
};

function ProgressPanel({ data }) {
  if (!data) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 text-sm text-slate-500 animate-pulse">
        Đang tải tiến trình…
      </div>
    );
  }

  const bySet = data.by_set || [];

  if (bySet.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 text-xs text-slate-400 text-center">
        <div className="text-3xl mb-2">📭</div>
        <div>Chưa có câu hỏi nào</div>
        <div className="text-[10px] mt-1 text-slate-300">Admin vào mục Câu hỏi để tạo</div>
      </div>
    );
  }

  // Tổng hợp: mastered + in_progress + total all sets
  const totalAll = bySet.reduce((s, x) => s + (x.total ?? 0), 0);
  const masteredAll = bySet.reduce((s, x) => s + (x.mastered ?? 0), 0);
  const inProgressAll = bySet.reduce((s, x) => s + (x.in_progress ?? 0), 0);
  const masteredPctAll = totalAll ? (masteredAll / totalAll) * 100 : 0;
  const inProgressPctAll = totalAll ? (inProgressAll / totalAll) * 100 : 0;
  const touchedPct = Math.round(masteredPctAll + inProgressPctAll);  // Tổng đã tiếp xúc

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Header with total progress */}
      <div className="bg-gradient-to-br from-brand-50 to-indigo-50 px-5 py-4 border-b border-slate-100">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-bold text-slate-800 flex items-center gap-1.5">
            <span>📊</span> Tiến trình học
          </h3>
          <span className="text-xs font-semibold text-brand-700 tabular-nums">
            {touchedPct}%
          </span>
        </div>
        {/* Stacked bar: mastered + in_progress + remaining */}
        <div className="h-1.5 bg-white/60 rounded-full overflow-hidden flex">
          <div
            className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all duration-700"
            style={{ width: `${masteredPctAll}%` }}
          />
          <div
            className="h-full bg-amber-400 transition-all duration-700"
            style={{ width: `${inProgressPctAll}%` }}
          />
        </div>
        <div className="text-[10px] text-slate-500 mt-1.5 tabular-nums">
          <InfoTooltip text="Một thẻ được coi là đã thuộc khi 2 lần liên tiếp không quên lần nào.">
            <span className="underline decoration-dotted decoration-slate-300 cursor-help">Đã thuộc</span>
          </InfoTooltip>
          {' '}<b className="text-emerald-700">{masteredAll}</b>
          {inProgressAll > 0 && (
            <>
              {' '}·{' '}
              <InfoTooltip text="Thẻ mới tiếp xúc hoặc vừa quên — chưa đủ 2 lần ôn đúng liên tiếp để lên 'Đã thuộc'.">
                <span className="underline decoration-dotted decoration-slate-300 cursor-help">Đang học</span>
              </InfoTooltip>
              {' '}<b className="text-amber-700">{inProgressAll}</b>
            </>
          )}
          {' '}/ {totalAll} thẻ toàn bộ
        </div>
      </div>

      {/* Bộ sections */}
      <div className="p-4 space-y-3">
        {bySet.map(s => <SetSection key={s.set} set={s} />)}
      </div>

      {/* Legend với tooltip giải thích SM-2 */}
      <div className="px-4 pb-4 pt-2 border-t border-slate-100 text-[10px] text-slate-500 leading-relaxed space-y-1">
        <div className="flex items-start gap-1.5">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block mt-1 shrink-0" />
          <span>
            <InfoTooltip text="Một thẻ được coi là đã thuộc khi 2 lần liên tiếp không quên lần nào.">
              <b className="text-emerald-700 underline decoration-dotted decoration-emerald-300 cursor-help">Đã thuộc</b>
            </InfoTooltip>
            {' '}— ôn đúng ≥2 lần liên tiếp → ôn cách ngày (1 → 6 → 15 ngày…)
          </span>
        </div>
        <div className="flex items-start gap-1.5">
          <span className="w-2 h-2 rounded-full bg-amber-400 inline-block mt-1 shrink-0" />
          <span>
            <InfoTooltip text="Thẻ mới tiếp xúc hoặc vừa quên — chưa đủ 2 lần ôn đúng liên tiếp để lên 'Đã thuộc'.">
              <b className="text-amber-700 underline decoration-dotted decoration-amber-300 cursor-help">Đang học</b>
            </InfoTooltip>
            {' '}— mới tiếp xúc hoặc vừa quên → ôn lại trong vài phút
          </span>
        </div>
        <div className="flex items-start gap-1.5">
          <span className="w-2 h-2 rounded-full bg-slate-300 inline-block mt-1 shrink-0" />
          <span>
            <b className="text-slate-600">Chưa học</b> — còn trong kho, chưa gặp thẻ này
          </span>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// InfoTooltip — popup nhẹ hover vào text có gạch chân dotted
// 2026-04-24: giải thích thuật ngữ SM-2 cho NV (Đã thuộc, Đang học).
// Dùng Tailwind group-hover để show/hide, không cần JS state.
// ============================================================
function InfoTooltip({ text, children }) {
  return (
    <span className="relative inline-block group">
      {children}
      <span
        role="tooltip"
        className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-1.5 w-56
                   rounded-lg bg-slate-800 text-white text-[11px] font-normal leading-snug
                   px-3 py-2 shadow-lg z-20
                   opacity-0 invisible group-hover:opacity-100 group-hover:visible
                   transition-opacity duration-150"
      >
        {text}
        {/* Arrow pointer */}
        <span className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0
                         border-l-4 border-r-4 border-t-4
                         border-l-transparent border-r-transparent border-t-slate-800" />
      </span>
    </span>
  );
}

function SetSection({ set }) {
  const total = set.total ?? 0;
  const mastered = set.mastered ?? 0;
  const inProgress = set.in_progress ?? 0;
  const masteredPct = total ? (mastered / total) * 100 : 0;
  const inProgressPct = total ? (inProgress / total) * 100 : 0;
  const touchedPct = Math.round(masteredPct + inProgressPct);  // % đã tiếp xúc
  // Auto-expand nếu có bất kỳ card nào đã học (mastered hoặc learning)
  const [open, setOpen] = useState(mastered > 0 || inProgress > 0);

  const topics = (set.topics || []).filter(t => (t.total ?? 0) > 0);
  const meta = SET_META[set.set] || DEFAULT_SET_META;

  return (
    <div className="group">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full text-left hover:bg-slate-50 -mx-2 px-2 py-1.5 rounded-lg transition-colors"
        type="button"
      >
        <div className="flex items-center justify-between text-xs mb-1.5">
          <span className="font-semibold text-slate-800 flex items-center gap-1.5">
            <span className={`text-[10px] text-slate-400 transition-transform inline-block ${open ? 'rotate-90' : ''}`}>
              ▸
            </span>
            <span className="text-base">{meta.icon}</span>
            <span>{boShort(set.set) || set.set}</span>
          </span>
          <span className="tabular-nums text-slate-600 text-[11px] flex items-center gap-1">
            {mastered > 0 && <b className="text-emerald-700">{mastered}</b>}
            {inProgress > 0 && <b className="text-amber-700">+{inProgress}</b>}
            {mastered === 0 && inProgress === 0 && <b>0</b>}
            <span className="text-slate-400">/{total}</span>
            {total > 0 && (
              <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${meta.pctPill}`}>
                {touchedPct}%
              </span>
            )}
          </span>
        </div>
        {/* Stacked progress bar: mastered (emerald) + in_progress (amber) + remaining (slate) */}
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden flex">
          <div
            className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all duration-500"
            style={{ width: `${masteredPct}%` }}
          />
          <div
            className="h-full bg-amber-400 transition-all duration-500"
            style={{ width: `${inProgressPct}%` }}
          />
        </div>
      </button>

      {open && topics.length > 0 && (
        <div className="mt-2 ml-5 pl-3 border-l-2 border-slate-100 space-y-2 py-1">
          {topics.map(t => (
            <TopicRow key={`${set.set}-${t.topic || '_no_topic'}`} topic={t} />
          ))}
        </div>
      )}
      {open && topics.length === 0 && (
        <div className="mt-1 ml-5 pl-3 text-[11px] text-slate-400 italic">
          (bộ này chưa có câu hỏi)
        </div>
      )}
    </div>
  );
}

function TopicRow({ topic }) {
  const total = topic.total ?? 0;
  const mastered = topic.mastered ?? 0;
  const inProgress = topic.in_progress ?? 0;
  const masteredPct = total ? (mastered / total) * 100 : 0;
  const inProgressPct = total ? (inProgress / total) * 100 : 0;

  return (
    <div>
      {/* M99 (2026-04-26): bỏ truncate max-w-[170px] để tên topic hiển thị đầy đủ
          (wrap nhiều dòng nếu dài). Số tổng giữ shrink-0 để không bị xuống dòng. */}
      <div className="flex items-start justify-between gap-2 text-[11px] text-slate-600 mb-1">
        <span className="break-words leading-snug" title={topic.topic || '(chưa phân chủ đề)'}>
          {topic.topic || '—'}
        </span>
        <span className="tabular-nums text-slate-500 shrink-0 flex items-center gap-1 mt-0.5">
          {mastered > 0 && (
            <span className="text-emerald-600 font-semibold">{mastered}</span>
          )}
          {inProgress > 0 && (
            <span className="text-amber-600 font-semibold">+{inProgress}</span>
          )}
          <span className="text-slate-300">/</span>
          <span className="text-slate-500">{total}</span>
        </span>
      </div>
      {/* Stacked bar: mastered → in-progress → remaining */}
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden flex">
        <div
          className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all duration-500"
          style={{ width: `${masteredPct}%` }}
        />
        <div
          className="h-full bg-amber-400 transition-all duration-500"
          style={{ width: `${inProgressPct}%` }}
        />
      </div>
    </div>
  );
}
